// src/pages/Citas.jsx
import { useState, useEffect, useCallback } from 'react';
import { getAppointments, addAppointment, updateAppointment, deleteAppointment, getEmployees } from '../data/store';

const APPT_TYPES = ['Medica', 'Dental', 'Legal', 'Personal', 'Vacaciones', 'Otro'];
const EMPTY_FORM = { employeeId: '', date: '', dateEnd: '', type: 'Medica', notes: '' };

const CALENDAR_ID = 'c_23a960b24e41e3481d85df141c6c3dff0602bb189021df14f1ad673479b02bfa@group.calendar.google.com';
const API_KEY = 'AIzaSyC_H6Xz-Y2DfebhKcBCEkNcIx1zR7GufMg';

async function fetchGoogleCalendarEvents() {
  const now = new Date();
  const timeMin = new Date(now.getFullYear() - 1, 0, 1).toISOString();
  const timeMax = new Date(now.getFullYear() + 2, 11, 31).toISOString();
  const base = 'https://www.googleapis.com/calendar/v3/calendars';
  const url = base + '/' + encodeURIComponent(CALENDAR_ID) + '/events?key=' + API_KEY + '&timeMin=' + timeMin + '&timeMax=' + timeMax + '&singleEvents=true&orderBy=startTime&maxResults=500';
  const res = await fetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || 'HTTP ' + res.status);
  }
  const data = await res.json();
  return data.items || [];
}

function groupByMonth(events) {
  const map = {};
  events.forEach(ev => {
    const start = ev.start?.dateTime || ev.start?.date || '';
    const d = new Date(start);
    const key = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0');
    const label = d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    if (!map[key]) map[key] = { key, label, events: [] };
    map[key].events.push(ev);
  });
  return Object.values(map).sort((a, b) => a.key.localeCompare(b.key));
}

function formatEventDate(ev) {
  const start = ev.start?.dateTime || ev.start?.date;
  const end = ev.end?.dateTime || ev.end?.date;
  const opts = { weekday: 'short', day: 'numeric', month: 'short' };
  const optsTime = { hour: '2-digit', minute: '2-digit' , hour12: true };
  if (!start) return '';
  const startD = new Date(start);
  const endD = end ? new Date(end) : null;
  if (ev.start?.date) {
    if (endD) {
      const endAdj = new Date(endD);
      endAdj.setDate(endAdj.getDate() - 1);
      if (startD.toDateString() === endAdj.toDateString()) {
        return startD.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) + ' (Todo el dia)';
      }
      return startD.toLocaleDateString('es-ES', opts) + ' - ' + endAdj.toLocaleDateString('es-ES', opts) + ' (Todo el dia)';
    }
    return startD.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) + ' (Todo el dia)';
  }
  let str = startD.toLocaleDateString('es-ES', opts) + ' ' + startD.toLocaleTimeString('es-ES', optsTime);
  if (endD) str += ' - ' + endD.toLocaleTimeString('es-ES', optsTime);
  return str;
}

function toInputDate(dateStr) {
  if (!dateStr) return '';
  const s = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [d, m, y] = s.split('/');
    return y + '-' + m + '-' + d;
  }
  return s;
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return '';
  const s = String(dateStr).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-');
    return d + '/' + m + '/' + y;
  }
  return s;
}

export default function Citas() {
  const [activeTab, setActiveTab] = useState('gcal');
  const [gcalMonths, setGcalMonths] = useState([]);
  const [gcalLoading, setGcalLoading] = useState(false);
  const [gcalError, setGcalError] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterEmp, setFilterEmp] = useState('');
  const [filterType, setFilterType] = useState('');
  const [localLoading, setLocalLoading] = useState(false);

  const loadGcal = useCallback(async () => {
    setGcalLoading(true);
    setGcalError(null);
    try {
      const evs = await fetchGoogleCalendarEvents();
      setGcalMonths(groupByMonth(evs));
    } catch (e) {
      setGcalError(e.message);
    } finally {
      setGcalLoading(false);
    }
  }, []);

  const loadLocal = useCallback(async () => {
    setLocalLoading(true);
    try {
      const [appts, emps] = await Promise.all([getAppointments(), getEmployees()]);
      setAppointments(appts || []);
      setEmployees(emps || []);
    } catch (e) {
      console.error('Error loading local data:', e);
      setAppointments([]);
      setEmployees([]);
    } finally {
      setLocalLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGcal();
    loadLocal();
  }, [loadGcal, loadLocal]);

  const handleSaveAppointment = async (e) => {
    e.preventDefault();
    if (!form.employeeId || !form.date) return;
    try {
      if (editingId) {
        await updateAppointment(editingId, form);
      } else {
        await addAppointment(form);
      }
      await loadLocal();
      setForm(EMPTY_FORM);
      setShowForm(false);
      setEditingId(null);
    } catch (err) {
      console.error('Error saving appointment:', err);
    }
  };

  const handleEdit = (appt) => {
    setForm({
      employeeId: String(appt.employee_id || appt.employeeId || ''),
      date: toInputDate(appt.date),
      dateEnd: toInputDate(appt.dateEnd || appt.date_end || ''),
      type: appt.type || 'Medica',
      notes: appt.notes || ''
    });
    setEditingId(appt.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteAppointment(id);
      await loadLocal();
    } catch (err) {
      console.error('Error deleting appointment:', err);
    }
  };

  const empName = (id) => {
    const emp = employees.find(e => e.id === id || e.id === Number(id));
    return emp ? emp.name : id;
  };

  const filteredAppts = (appointments || []).filter(a => {
    const matchEmp = !filterEmp || String(a.employeeId || a.employee_id) === String(filterEmp);
    const matchType = !filterType || a.type === filterType;
    return matchEmp && matchType;
  });

  const s = {
    wrap: { padding: '24px', maxWidth: 900, margin: '0 auto', fontFamily: 'Inter,sans-serif', color: '#e0e0e0' },
    hdr: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
    h1: { fontSize: 26, fontWeight: 700, color: '#fff', margin: 0 },
    tabs: { display: 'flex', gap: 8, marginBottom: 24, borderBottom: '1px solid #333' },
    tab: (a) => ({ padding: '10px 20px', background: 'none', border: 'none', borderBottom: a ? '2px solid #4f9cf9' : '2px solid transparent', color: a ? '#4f9cf9' : '#888', fontWeight: a ? 700 : 400, cursor: 'pointer', fontSize: 15, marginBottom: -1 }),
    btn: { padding: '10px 20px', background: '#4f9cf9', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 },
    btnSm: { padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12 },
    card: { background: '#1a1a1a', borderRadius: 14, padding: '20px 24px', marginBottom: 16, border: '1px solid #2a2a2a' },
    mhdr: { fontSize: 18, fontWeight: 700, color: '#4f9cf9', marginBottom: 12, marginTop: 24, textTransform: 'capitalize', borderBottom: '1px solid #2a2a2a', paddingBottom: 8 },
  };

  return (
    <div style={s.wrap}>
      <div style={s.hdr}>
        <h1 style={s.h1}>Citas y Solicitudes</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          {activeTab === 'gcal' && (
            <button style={s.btn} onClick={loadGcal}>Actualizar Calendar</button>
          )}
          {activeTab === 'local' && (
            <button style={s.btn} onClick={() => { setShowForm(!showForm); setEditingId(null); setForm(EMPTY_FORM); }}>
              {showForm ? 'Cancelar' : '+ Nueva Cita'}
            </button>
          )}
        </div>
      </div>

      <div style={s.tabs}>
        <button style={s.tab(activeTab === 'gcal')} onClick={() => setActiveTab('gcal')}>Google Calendar</button>
        <button style={s.tab(activeTab === 'local')} onClick={() => setActiveTab('local')}>Citas Locales</button>
      </div>

      {activeTab === 'gcal' && (
        <div>
          {gcalError && (
            <div style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.4)', borderRadius: 10, padding: '16px 20px', marginBottom: 20, color: '#f87171' }}>
              <strong>Error:</strong> {gcalError}
              <button onClick={loadGcal} style={{ marginLeft: 0, marginTop: 12, display: 'block', padding: '6px 16px', borderRadius: 6, background: 'rgba(220,38,38,0.3)', color: '#f87171', border: 'none', cursor: 'pointer', fontSize: 12 }}>Reintentar</button>
            </div>
          )}
          {gcalLoading && (
            <div style={{ ...s.card, textAlign: 'center', padding: '40px' }}>
              <h2 style={{ color: '#888', margin: 0 }}>Cargando eventos...</h2>
            </div>
          )}
          {!gcalLoading && !gcalError && gcalMonths.length === 0 && (
            <div style={{ ...s.card, textAlign: 'center', padding: '40px' }}>
              <h2 style={{ color: '#888', margin: 0 }}>No hay eventos en el calendario</h2>
            </div>
          )}
          {!gcalLoading && gcalMonths.map(month => (
            <div key={month.key}>
              <div style={s.mhdr}>{month.label} ({month.events.length} eventos)</div>
              {month.events.map(ev => (
                <div key={ev.id} style={s.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, color: '#fff' }}>{ev.summary || '(Sin titulo)'}</div>
                      <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{formatEventDate(ev)}</div>
                      {ev.description && <div style={{ fontSize: 13, color: '#aaa', marginTop: 6 }}>{ev.description}</div>}
                    </div>
                    <a href={ev.htmlLink} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 12, padding: '4px 12px', borderRadius: 6, background: '#1e3a5f', color: '#4f9cf9', textDecoration: 'none', fontSize: 12, fontWeight: 600 }}>Ver</a>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'local' && (
        <div>
          {showForm && (
            <div style={{ ...s.card, marginBottom: 24 }}>
              <h3 style={{ color: '#fff', margin: '0 0 16px' }}>{editingId ? 'Editar Cita' : 'Nueva Cita Local'}</h3>
              <form onSubmit={handleSaveAppointment} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ color: '#888', fontSize: 12, display: 'block', marginBottom: 4 }}>Empleado</label>
                  <select value={form.employeeId} onChange={e => setForm({ ...form, employeeId: e.target.value })} required style={{ width: '100%', padding: '8px 12px', background: '#2a2a2a', border: '1px solid #444', borderRadius: 6, color: '#fff', fontSize: 14 }}>
                    <option value="">Seleccionar empleado</option>
                    {(employees || []).map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ color: '#888', fontSize: 12, display: 'block', marginBottom: 4 }}>Tipo</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={{ width: '100%', padding: '8px 12px', background: '#2a2a2a', border: '1px solid #444', borderRadius: 6, color: '#fff', fontSize: 14 }}>
                    {APPT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ color: '#888', fontSize: 12, display: 'block', marginBottom: 4 }}>Fecha inicio</label>
                  <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required style={{ width: '100%', padding: '8px 12px', background: '#2a2a2a', border: '1px solid #444', borderRadius: 6, color: '#fff', fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ color: '#888', fontSize: 12, display: 'block', marginBottom: 4 }}>Fecha fin (si aplica)</label>
                  <input type="date" value={form.dateEnd} onChange={e => setForm({ ...form, dateEnd: e.target.value })} style={{ width: '100%', padding: '8px 12px', background: '#2a2a2a', border: '1px solid #444', borderRadius: 6, color: '#fff', fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ color: '#888', fontSize: 12, display: 'block', marginBottom: 4 }}>Notas</label>
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Notas..." style={{ width: '100%', padding: '8px 12px', background: '#2a2a2a', border: '1px solid #444', borderRadius: 6, color: '#fff', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
                  <button type="submit" style={s.btn}>{editingId ? 'Actualizar Cita' : 'Guardar Cita'}</button>
                  <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setEditingId(null); }} style={{ ...s.btn, background: '#333' }}>Cancelar</button>
                </div>
              </form>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <select value={filterEmp} onChange={e => setFilterEmp(e.target.value)} style={{ padding: '8px 12px', background: '#2a2a2a', border: '1px solid #444', borderRadius: 6, color: '#fff', fontSize: 13 }}>
              <option value="">Todos los empleados</option>
              {(employees || []).map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: '8px 12px', background: '#2a2a2a', border: '1px solid #444', borderRadius: 6, color: '#fff', fontSize: 13 }}>
              <option value="">Todos los tipos</option>
              {APPT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {localLoading ? (
            <div style={{ ...s.card, textAlign: 'center', padding: '40px' }}>
              <h3 style={{ color: '#888', margin: 0 }}>Cargando citas...</h3>
            </div>
          ) : filteredAppts.length === 0 ? (
            <div style={{ ...s.card, textAlign: 'center', padding: '40px' }}>
              <h3 style={{ color: '#888', margin: 0 }}>No hay citas locales</h3>
              <p style={{ color: '#666', marginTop: 8 }}>Haz clic en "+ Nueva Cita" para agregar una.</p>
            </div>
          ) : (
            filteredAppts.map(appt => (
              <div key={appt.id} style={s.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 15 }}>{empName(appt.employeeId || appt.employee_id)}</div>
                    <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
                      {appt.type} - Inicio: {formatDisplayDate(appt.date)}
                      {(appt.dateEnd || appt.date_end) ? ' - Fin: ' + formatDisplayDate(appt.dateEnd || appt.date_end) : ''}
                    </div>
                    {appt.notes && <div style={{ color: '#aaa', fontSize: 12, marginTop: 4 }}>{appt.notes}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => handleEdit(appt)} style={{ ...s.btnSm, background: 'rgba(79,156,249,0.2)', color: '#4f9cf9' }}>Editar</button>
                    <button onClick={() => handleDelete(appt.id)} style={{ ...s.btnSm, background: 'rgba(220,38,38,0.2)', color: '#f87171' }}>Eliminar</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
